import type {
	FertilizationBase,
	MaintenanceSectorFertilization,
	MaintenanceSectorRecommendation,
} from '../types';
import { EXECUTION_PART_COUNT } from '../constants/maintenance.constants';
import { normalizeMaintenanceMonth } from './maintenanceAdviceMapper';

export interface MaintenanceExecutionProduct {
	_id: string;
	name?: string;
	commercialName?: string;
	nature?: string;
	doseMeasurementUnit?: string;
}

export interface ExecutionTableLine {
	fertilizer: string;
	quantity: number | null;
	parts: Array<number | null>;
	total: number | null;
}

export interface ExecutionDateGroup {
	date: string;
	lines: ExecutionTableLine[];
}

export interface ExecutionPeriodTable {
	groups: ExecutionDateGroup[];
	totals: {
		quantity: number;
		parts: number[];
		total: number;
	};
}

const emptyTotals = (): ExecutionPeriodTable['totals'] => ({
	quantity: 0,
	parts: Array.from({ length: EXECUTION_PART_COUNT }, () => 0),
	total: 0,
});

const isoDate = (value: string) => value.slice(0, 10);

const sortedDates = (dates: (string | null)[]) =>
	dates.filter((date): date is string => !!date).map(isoDate).sort();

const formatDayMonth = (value: string) => {
	const [, month, day] = isoDate(value).split('-');
	return month && day ? `${day}/${month}` : value;
};

const padParts = (parts: number[]): Array<number | null> =>
	Array.from({ length: EXECUTION_PART_COUNT }, (_, index) => parts[index] ?? null);

const sum = (values: Array<number | null | undefined>) =>
	values.reduce<number>((acc, value) => acc + (value ?? 0), 0);

export const currentYearMonth = (now = new Date()) =>
	`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

export const isUpcomingPeriod = (period: string, now = new Date()) => {
	const normalized = normalizeMaintenanceMonth(period);
	return !!normalized && normalized > currentYearMonth(now);
};

const asRecommendationList = (
	sector: MaintenanceSectorFertilization
): MaintenanceSectorRecommendation[] => {
	if (Array.isArray(sector.recommendations)) return sector.recommendations;
	if (sector.recommendations) return [sector.recommendations];
	return [];
};

const periodFirstDay = (period?: string) => {
	const month = normalizeMaintenanceMonth(period);
	return month ? `${month}-01` : '';
};

const mapIrrigationRecommendation = (
	recommendation: MaintenanceSectorRecommendation
): FertilizationBase[] => {
	const proposal = recommendation.proposal ?? recommendation.fertilizers ?? [];
	if (!proposal.length) return [];

	const eventDates = [
		...new Set(
			(recommendation.boxes ?? [])
				.flatMap((box) => box.events ?? [])
				.map((event) => event.date)
				.filter((date): date is string => !!date)
		),
	].sort();
	const dates = eventDates.length ? eventDates : [periodFirstDay(recommendation.period)].filter(Boolean);
	if (!dates.length) return [];

	const loadsByDate = new Map<string, number>();
	(recommendation.boxes ?? []).forEach((box) => {
		(box.events ?? []).forEach((event) => {
			if (!event.date) return;
			loadsByDate.set(event.date, Math.max(loadsByDate.get(event.date) ?? 1, event.loads ?? 1));
		});
	});

	return dates.flatMap((date) => {
		const loads = loadsByDate.get(date) ?? 1;
		const doseFactor = 1 / dates.length / loads;
		return Array.from({ length: loads }, () => ({
			method: 'irrigation' as const,
			dates: [date],
			fertilizers: proposal.map((item) => ({
				id: item.id,
				dose: Number(item.dose) * doseFactor,
				name: item.name,
			})),
		}));
	});
};

export const mapSectorFertilizationsToApplications = (
	items: MaintenanceSectorFertilization[] = []
): FertilizationBase[] => {
	const firstByMethod = new Map<string, MaintenanceSectorFertilization>();
	items.forEach((item) => {
		if (!firstByMethod.has(item.method)) firstByMethod.set(item.method, item);
	});

	return [...firstByMethod.values()].flatMap((sector) => {
		if (sector.method !== 'irrigation') return [];
		return asRecommendationList(sector).flatMap(mapIrrigationRecommendation);
	});
};

export const mapMaintenanceExecutionPeriod = (
	fertilizations: FertilizationBase[] = [],
	products: MaintenanceExecutionProduct[] = [],
	month: string
): ExecutionPeriodTable => {
	const monthKey = normalizeMaintenanceMonth(month);
	if (!monthKey) {
		return { groups: [], totals: emptyTotals() };
	}

	const productsById = new Map(products.map((product) => [product._id, product]));
	const groupsByDate = new Map<
		string,
		Map<string, { name: string; quantity: number | null; parts: number[] }>
	>();

	fertilizations.forEach((fertilization) => {
		const datesInMonth = sortedDates(fertilization.dates).filter(
			(date) => date.slice(0, 7) === monthKey
		);
		if (datesInMonth.length === 0) return;

		datesInMonth.forEach((date) => {
			if (!groupsByDate.has(date)) {
				groupsByDate.set(date, new Map());
			}
			const fertilizersById = groupsByDate.get(date);
			if (!fertilizersById) return;

			fertilization.fertilizers.forEach(({ id, dose }) => {
				if (!id) return;
				const product = productsById.get(id);
				const name = product?.name || product?.commercialName || 'Produto não encontrado';
				const existing = fertilizersById.get(id);
				const nextParts = existing?.parts ? [...existing.parts] : [];
				if (dose != null && Number.isFinite(dose)) {
					nextParts.push(dose);
				}
				fertilizersById.set(id, {
					name,
					quantity: existing?.quantity ?? (dose != null && Number.isFinite(dose) ? dose : null),
					parts: nextParts,
				});
			});
		});
	});

	const groups: ExecutionDateGroup[] = [...groupsByDate.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([date, fertilizersById]) => ({
			date: formatDayMonth(date),
			lines: [...fertilizersById.values()].map((line) => ({
				fertilizer: line.name,
				quantity: line.quantity,
				parts: padParts(line.parts),
				total: line.parts.length > 0 ? sum(line.parts) : null,
			})),
		}));

	const totals = groups.reduce(
		(acc, group) => {
			group.lines.forEach((line) => {
				acc.quantity += line.quantity ?? 0;
				acc.total += line.total ?? 0;
				line.parts.forEach((part, index) => {
					acc.parts[index] += part ?? 0;
				});
			});
			return acc;
		},
		emptyTotals()
	);

	return { groups, totals };
};
